/**
 * The owner report's reads, shared by the page and the app entry. A Telegram launch starts
 * them before React renders, while the page's code (and its charts) still loads (owner
 * 24.10.2026: «долго ждал»); the page asks the same queries and finds them in flight or
 * done.
 */
import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import {
  getOwnerDashboard,
  getOwnerDefaultDay,
  signInOwnerViewer,
  type OwnerPeriod,
} from '@/api/ownerReport';

import { openingDay } from './ownerReportModel';
import {
  readStoredViewer,
  readTelegramInitData,
  storeViewer,
  type StoredViewer,
} from './telegramMiniApp';

const OWNER_PATH = '/webadmin/app/owner';

// The launch data opens one session and a repeated sign-in with it is refused: it is sent
// once per page, even when the query cache is cleared in between.
let signIns = new Map<string, Promise<StoredViewer>>();

export function signInQuery(initData: string | null) {
  return queryOptions({
    queryKey: ['owner-report', 'sign-in', initData],
    queryFn: () => {
      const launch = initData ?? '';
      let viewer = signIns.get(launch);
      if (!viewer) {
        viewer = signInOwnerViewer(launch).then((session) => {
          const stored = { token: session.token, expiresAt: session.expires_at };
          storeViewer(stored);
          return stored;
        });
        signIns.set(launch, viewer);
      }
      return viewer;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

/** The day the report opens on without a date: before noon the last day with sales. */
export function defaultDayQuery(token: string | null) {
  return queryOptions({
    queryKey: ['owner-report', 'default-day', token],
    queryFn: () => getOwnerDefaultDay(token),
    staleTime: 60_000,
    retry: false,
  });
}

export function dashboardQuery(date: string, period: OwnerPeriod, token: string | null) {
  return queryOptions({
    queryKey: ['owner-report', 'dashboard', date, period, token],
    queryFn: () => getOwnerDashboard(date, period, token),
    staleTime: 60_000,
    retry: (failures, error) =>
      !(error instanceof ApiError && error.statusCode < 500) && failures < 2,
  });
}

/**
 * From the entry, before the first render: a Telegram launch of the plain report (the bot's
 * button) signs in and reads the day's dashboard. A browser reads with the cookie session,
 * which the page waits for, and other sections and periods load with the page.
 */
export function startOwnerReport(client: QueryClient, location: Location = window.location) {
  if (location.pathname.replace(/\/$/, '') !== OWNER_PATH) return;
  const params = new URLSearchParams(location.search);
  if (params.has('section') || params.has('period')) return;
  const initData = readTelegramInitData(location.hash);
  const stored = readStoredViewer(new Date());
  if (!initData && !stored) return;
  void (async () => {
    const token = stored?.token ?? (await client.fetchQuery(signInQuery(initData))).token;
    const date =
      params.get('date') ??
      openingDay(new Date()) ??
      (await client.fetchQuery(defaultDayQuery(token))).date;
    await client.prefetchQuery(dashboardQuery(date, 'day', token));
  })().catch(() => {
    // The page asks again and shows what failed.
  });
}

export function __resetOwnerLaunchForTests(): void {
  signIns = new Map();
}
